import { API_BASE_URL } from '$lib/config';
import { getAccessToken, refreshSession, setAccessToken } from './auth';
import { APIError } from './errors';

export async function apiFetch<T>(
  endpoint: string,
  options: RequestInit = {},
  retried = false
): Promise<T> {
  const token = getAccessToken();
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    credentials: 'include',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers as Record<string, string> | undefined)
    }
  });

  if (response.status === 401 && !retried && !endpoint.startsWith('/auth/')) {
    try {
      await refreshSession();
      return apiFetch<T>(endpoint, options, true);
    } catch {
      setAccessToken(null);
      throw new Error('Authentication required. Please log in again.');
    }
  }

  if (!response.ok) {
    const errorData: unknown = await response.json().catch(() => ({}));
    const detail =
      typeof errorData === 'object' && errorData !== null && 'detail' in errorData
        ? String((errorData as { detail?: unknown }).detail)
        : null;
    throw new APIError(detail || `HTTP ${response.status}: ${response.statusText}`, response.status, errorData);
  }

  if (response.status === 204) return null as T;
  return (await response.json()) as T;
}

export function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error ?? 'An unexpected error occurred.');
}

// apiUpload posts a multipart/form-data body (e.g. an issue photo) — never
// routed through apiFetch, which always forces `Content-Type: application/json`
// and would otherwise strip the multipart boundary the browser sets.
export async function apiUpload<T>(endpoint: string, form: FormData, retried = false): Promise<T> {
  const token = getAccessToken();
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: 'POST',
    credentials: 'include',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form
  });

  if (response.status === 401 && !retried) {
    try {
      await refreshSession();
      return apiUpload<T>(endpoint, form, true);
    } catch {
      setAccessToken(null);
      throw new Error('Authentication required. Please log in again.');
    }
  }

  if (!response.ok) {
    const errorData: unknown = await response.json().catch(() => ({}));
    const detail =
      typeof errorData === 'object' && errorData !== null && 'detail' in errorData
        ? String((errorData as { detail?: unknown }).detail)
        : null;
    throw new Error(detail || `HTTP ${response.status}: ${response.statusText}`);
  }
  return (await response.json()) as T;
}

// apiFetchBlob is the authenticated equivalent of an <img src="..."> fetch —
// used for issue attachments, which are served through auth (see
// GET /api/attachments/:id/file) rather than as static files, so a plain
// <img> tag (which can't send a bearer header) can't load them directly.
// Callers should revoke the returned object URL when done with it.
export async function apiFetchBlob(endpoint: string, retried = false): Promise<string> {
  const token = getAccessToken();
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    credentials: 'include',
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });

  if (response.status === 401 && !retried) {
    try {
      await refreshSession();
      return apiFetchBlob(endpoint, true);
    } catch {
      setAccessToken(null);
      throw new Error('Authentication required. Please log in again.');
    }
  }

  if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  const blob = await response.blob();
  return URL.createObjectURL(blob);
}

// apiDownload authenticated-fetches endpoint — following the redirect to a
// presigned S3 URL transparently, the same way a browser navigation would —
// and saves the response as filename via a throwaway object-URL anchor.
// Used for deal attachment downloads (GET
// /api/deals/:id/attachments/:id/download), which need a bearer token to
// reach but then 302 to an unauthenticated presigned URL.
export async function apiDownload(endpoint: string, filename: string, retried = false): Promise<void> {
  const token = getAccessToken();
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    credentials: 'include',
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });

  if (response.status === 401 && !retried) {
    try {
      await refreshSession();
      return apiDownload(endpoint, filename, true);
    } catch {
      setAccessToken(null);
      throw new Error('Authentication required. Please log in again.');
    }
  }

  if (!response.ok) {
    const errorData: unknown = await response.json().catch(() => ({}));
    const detail =
      typeof errorData === 'object' && errorData !== null && 'detail' in errorData
        ? String((errorData as { detail?: unknown }).detail)
        : null;
    throw new Error(detail || `HTTP ${response.status}: ${response.statusText}`);
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
